import React, { useState, useEffect } from 'react'
import { Search, User, X, Phone, TrendingUp } from 'lucide-react'
import { Input } from '../../ui/Input'
import { useLeads } from '../../../hooks/useLeads'

interface LeadSearchFieldProps {
 value: string
 onChange: (leadId: string) => void
 placeholder?: string
 disabled?: boolean
}

export const LeadSearchField: React.FC<LeadSearchFieldProps> = ({
 value,
 onChange,
 placeholder = "Buscar cliente...",
 disabled = false
}) => {
 const { leads } = useLeads()
 const [searchTerm, setSearchTerm] = useState('')
 const [showResults, setShowResults] = useState(false)
 const [selectedLead, setSelectedLead] = useState<any>(null)
 const [filteredLeads, setFilteredLeads] = useState<any[]>([])

 // Filtrar leads localmente quando o termo de busca muda
 useEffect(() => {
 if (searchTerm.length >= 2) {
 const filtered = leads.filter(lead => {
 const searchLower = searchTerm.toLowerCase()
 const nameMatch = lead.name.toLowerCase().includes(searchLower)
 const emailMatch = lead.emails?.some((e: any) => e.email.toLowerCase().includes(searchLower))
 const phoneDigits = searchTerm.replace(/\D/g, '')
 const phoneMatch = phoneDigits && lead.phones?.some((p: any) =>
 `${p.ddd}${p.phone}`.replace(/\D/g, '').includes(phoneDigits)
 )
 return nameMatch || emailMatch || phoneMatch
 })
 setFilteredLeads(filtered.slice(0, 10)) // Limitar a 10 resultados
 setShowResults(true)
 } else {
 setFilteredLeads([])
 setShowResults(false)
 }
 }, [searchTerm, leads])

 // Encontrar lead selecionado
 useEffect(() => {
 if (value && leads.length > 0) {
 const lead = leads.find(l => l.id === value)
 if (lead) {
 setSelectedLead(lead)
 }
 }
 }, [value, leads])

 const handleLeadSelect = (lead: any) => {
 setSelectedLead(lead)
 onChange(lead.id)
 setSearchTerm('')
 setShowResults(false)
 }

 const handleClear = () => {
 setSelectedLead(null)
 onChange('')
 setSearchTerm('')
 setShowResults(false)
 }

 const handleInputFocus = () => {
 if (searchTerm.length >= 2) {
 setShowResults(true)
 }
 }

 return (
 <div className="relative">
 <div className="relative">
 <div className="relative">
 <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
 <Input
 value={selectedLead ? selectedLead.name : searchTerm}
 onChange={(e) => {
 if (selectedLead) {
 handleClear()
 } else {
 setSearchTerm(e.target.value)
 }
 }}
 onFocus={handleInputFocus}
 placeholder={placeholder}
 disabled={disabled}
 className="pl-10 pr-10"
 />
 {selectedLead && (
 <button
 type="button"
 onClick={handleClear}
 className="absolute right-3 top-1/2 transform -translate-y-1/2 p-1 hover:bg-surface rounded"
 >
 <X className="h-4 w-4 text-muted-foreground" />
 </button>
 )}
 </div>
 </div>

 {/* Resultados da busca */}
 {showResults && searchTerm.length >= 2 && (
 <div className="absolute z-50 w-full mt-1 bg-card border border-border rounded-lg shadow-lg max-h-60 overflow-y-auto">
 {filteredLeads.length > 0 ? (
 <div className="py-1">
 {filteredLeads.map((lead) => (
 <button
 key={lead.id}
 type="button"
 onClick={() => handleLeadSelect(lead)}
 className="w-full px-4 py-3 text-left hover:bg-surface focus:bg-surface dark:focus:bg-gray-700 focus:outline-none"
 >
 <div className="flex items-start gap-3">
 <div className="w-10 h-10 bg-accent/10 dark:bg-primary-900 rounded-full flex items-center justify-center flex-shrink-0">
 <User className="h-5 w-5 text-accent dark:text-accent" />
 </div>
 <div className="flex-1 min-w-0">
 <div className="flex items-center gap-2">
 <p className="font-medium text-foreground truncate">
 {lead.name}
 </p>
 </div>
 <div className="mt-1 flex items-center gap-3 text-sm text-muted-foreground">
 {lead.phones && lead.phones.length > 0 && (
 <div className="flex items-center gap-1.5">
 <Phone className="h-3 w-3 flex-shrink-0" />
 <span>({lead.phones[0].ddd}) {lead.phones[0].phone}</span>
 </div>
 )}
 {lead.funnelStage && (
 <div className="flex items-center gap-1.5">
 <TrendingUp className="h-3 w-3 flex-shrink-0" />
 <span className="truncate">{lead.funnelStage.name}</span>
 </div>
 )}
 </div>
 </div>
 </div>
 </button>
 ))}
 </div>
 ) : (
 <div className="p-3 text-center text-sm text-muted-foreground">
 Nenhum cliente encontrado
 </div>
 )}
 </div>
 )}
 </div>
 )
}
